import { useDispatch, useSelector } from 'react-redux';
import {
	useEffect, useRef, useState,
} from 'react';
import { baseShot } from '../stores/reducers/shots';
import { setShotModalShot } from '../stores/actions/shots';
import { publishDraft, discardDraft } from '../connection/generate-content';
import { GreenroomState as GRS } from '../stores/types/types';
import Socket from '../connection/socket';
import { setModalOpen } from '../stores/actions/modal';
import { clearDraftOverlays } from '../stores/actions/graphics';
import { clearDraftContent } from '../stores/actions/generate-content';
import { ContentServiceJobStatus, FkTable } from '../stores/types/content';
import { clearDraftMedia } from '../stores/actions/media';
import { ClearChipsetDraft } from '../stores/actions/content-chips';

export const useCloseEditModal = () => {
	const dispatch = useDispatch();
	const meeting = useSelector((state: GRS) => state.SessionReducer.meeting);
	const token = useSelector((state: GRS) => state.SessionReducer.token);
	const session = useSelector((state: GRS) => state.SessionReducer.session);
	const connectedUsers = useSelector((state: GRS) => state.SessionReducer.connectedUsers);
	const shotModalShot = useSelector((state: GRS) => state.ShotsReducer.shotModalShot);

	const closeEditModal = async (socket: Socket | null | undefined, doSave: boolean) => {
		const otherUsersWithShotModal = connectedUsers.filter((u) => !!u.shotModalId && u.session !== session);
		socket?.disconnectEditListeners();
		if (token && meeting?.meeting) {
			if (doSave) {
				// only publish media and overlays that are in the current shot.
				const { shot_data: { shotFeeds, shotGraphics } } = shotModalShot;
				const overlayIds = shotGraphics ?? [];
				const mediaIds = shotFeeds.flatMap(f => f.media ?? []);
				publishDraft(token, meeting.meeting, session ?? '', overlayIds, mediaIds);
			} else if (!doSave && otherUsersWithShotModal.length === 0) {
				// only discard drafts if there are no other users with the shot modal open
				discardDraft(token, meeting.meeting);
			}
		}
		dispatch(clearDraftOverlays());
		dispatch(clearDraftMedia());
		dispatch(clearDraftContent());
		dispatch(ClearChipsetDraft(null));
		dispatch(setShotModalShot(baseShot));
		dispatch(setModalOpen(false));
	};

	return closeEditModal;
};

export const useShotEditModalIsLocked = () => {
	const [didJustEdit, setDidJustEdit] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	const contentEdits = useSelector((s: GRS) => s.GeneratedContentReducer.generatedContentEdits);
	const shotModalShot = useSelector((s: GRS) => s.ShotsReducer.shotModalShot);
	const shotModalEditTimestamp = useSelector((s: GRS) => s.ShotsReducer.shotModalEditTimestamp);
	const upsertingContent = useSelector((s: GRS) => s.GeneratedContentReducer.upsertingContent);

	const editTimeout = useRef<NodeJS.Timeout>();

	useEffect(() => {
		if (shotModalEditTimestamp > 0) {
			clearTimeout(editTimeout.current);
			setDidJustEdit(true);
			setTimeout(() => {
				setDidJustEdit(false);
			}, 1000 * 2);
		}
	}, [shotModalEditTimestamp]);

	useEffect(() => {
		const overlayIds = shotModalShot.shot_data.shotGraphics ?? [];
		const feedMediaIds = (shotModalShot.shot_data.shotFeeds ?? [])
			.map(feed => feed.media)
			.filter(Boolean);

		const overlaysProcessing = overlayIds.some(id =>
			contentEdits.some(
				gc =>
					gc.fkId === id &&
					gc.fkTable === FkTable.graphics &&
					gc.jobStatus === ContentServiceJobStatus.processing
			)
		);

		const mediaProcessing = feedMediaIds.some(id =>
			contentEdits.some(
				gc =>
					gc.fkId === id &&
					gc.fkTable === FkTable.media &&
					gc.jobStatus === ContentServiceJobStatus.processing
			)
		);

		setIsProcessing(overlaysProcessing || mediaProcessing);
	}, [
		shotModalShot.shot_data.shotGraphics,
		shotModalShot.shot_data.shotFeeds,
		contentEdits,
	]);

	return (didJustEdit || isProcessing || upsertingContent);
};

export default useCloseEditModal;
